import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { controlledTypeStyle } from "../lib/typography";
import TiltCard from "./TiltCard";

function fontSize(value, fallback, min, max) {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  return `${safe}px`;
}

export default function Gallery() {
  const { content } = useContent();
  const [sections, setSections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("all");
  const eyebrow = content?.gallery_eyebrow ?? "⸺ Selected Works";
  const heading = content?.gallery_heading ?? "The archive";

  useEffect(() => {
    (async () => {
      try {
        const [secRes, projRes] = await Promise.all([
          api.get("/sections"),
          api.get("/projects"),
        ]);
        setSections(secRes.data);
        setProjects(projRes.data);
      } catch (e) {
        console.error("Failed to load gallery", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered =
    activeSection === "all"
      ? projects
      : projects.filter((p) => p.section_id === activeSection);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="scroll-effect-section site-section-pad-lg relative py-24 md:py-32"
    >
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/50 mb-4">
              {eyebrow}
            </div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tightest leading-[0.95]">
              <span
                style={{
                  ...controlledTypeStyle({
                    fontSize: fontSize(content?.typography_archive_heading_size, 60, 20, 88),
                    font: content?.typography_archive_heading_font,
                  }),
                  lineHeight: 0.95,
                }}
              >
                {heading}
              </span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="section-filters">
            <button
              onClick={() => setActiveSection("all")}
              data-testid="filter-all"
              className={`site-button font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
                activeSection === "all"
                  ? "bg-bone-50 text-ink-900 border-bone-50"
                  : "border-bone-50/20 text-bone-200/70 hover:border-bone-50/60"
              }`}
            >
              All / {projects.length}
            </button>
            {sections.map((s) => {
              const count = projects.filter((p) => p.section_id === s.id).length;
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSection(s.id)}
                  data-testid={`filter-section-${s.slug}`}
                  className={`site-button font-mono text-[11px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
                    activeSection === s.id
                      ? "bg-bone-50 text-ink-900 border-bone-50"
                      : "border-bone-50/20 text-bone-200/70 hover:border-bone-50/60"
                  }`}
                >
                  {s.name} / {count}
                </button>
              );
            })}
          </div>
        </div>

        {loading && (
          <div
            data-testid="gallery-loading"
            className="text-bone-200/50 font-mono text-xs uppercase tracking-[0.3em] py-20 text-center"
          >
            Loading archive…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div
            data-testid="gallery-empty"
            className="border border-dashed border-bone-50/10 p-20 text-center"
          >
            <p className="font-display text-3xl mb-3">The archive is empty.</p>
            <p className="text-bone-200/50 text-sm">
              Head to <span className="text-ember">/admin</span> to publish your
              first project.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {filtered.map((project, i) => (
            <div
              key={project.id}
              className="animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <TiltCard project={project} index={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
